import type Highcharts from "highcharts"
import type { CategoryChartSeries, DateChartSeries, PieChartData } from "./graphs/SuperChart"
import { ChartType, DateType, LegendAlignment } from "~/constants/graphs"
import { calculateLinearRegression } from "./graphs/utils/GraphSeriesUtils"
import { getTailwindValue } from "~/utils/tailwind"
import { DateFormatType, formatDate } from "~/utils/datetime"

interface ChatChartData {
  type: "bar" | "line" | "area" | "pie"
  title?: string
  series: {
    name: string
    yAxis?: 0 | 1
    data: { label: string; value: number }[]
  }[]
  showTrend?: boolean
}

export function parseChatChart(json: string): ChatChartData | null {
  try {
    const parsed = JSON.parse(json)
    if (!parsed || typeof parsed !== "object") return null
    if (!["bar", "line", "area", "pie"].includes(parsed.type)) return null
    if (!Array.isArray(parsed.series) || parsed.series.length === 0) return null
    for (const s of parsed.series) {
      if (!s.name || !Array.isArray(s.data)) return null
      if (s.yAxis !== undefined && s.yAxis !== 0 && s.yAxis !== 1) return null
      for (const d of s.data) {
        if (typeof d.label !== "string" || typeof d.value !== "number") return null
      }
    }
    return {
      type: parsed.type,
      title: parsed.title,
      series: parsed.series,
      showTrend: parsed.showTrend === true,
    } as ChatChartData
  } catch {
    return null
  }
}

interface BarChartResult {
  type: typeof ChartType.CategorySeries
  data: CategoryChartSeries[]
  height: number
  disableAnimation: boolean
  noLegend: boolean
  legendAlignment?: LegendAlignment
  title?: string
  showCurrency: boolean
  autoFitXLabels: boolean
}

interface LineChartResult {
  type: typeof ChartType.TimeSeries
  data: DateChartSeries[]
  height: number
  disableAnimation: boolean
  noLegend: boolean
  legendAlignment?: LegendAlignment
  dateType: DateType
  title?: string
  showCurrency: boolean
  xAxisLabelFormatter: (this: Highcharts.AxisLabelsFormatterContextObject) => string
  xAxisTickInterval: number
  xAxisLabelOverflow: "justify"
}

const DAY_MS = 24 * 60 * 60 * 1000
const WEEK_MS = 7 * DAY_MS
const HIGHCHARTS_MONTH_MS = 28 * DAY_MS
const HIGHCHARTS_YEAR_MS = 364 * DAY_MS
const TARGET_TICK_COUNT = 9

const HIGHCHARTS_TICK_STEPS_MS = [
  ...[1, 2].map((n) => n * DAY_MS),
  ...[1, 2].map((n) => n * WEEK_MS),
  ...[1, 2, 3, 4, 6].map((n) => n * HIGHCHARTS_MONTH_MS),
  ...[1, 2, 5, 10, 20, 50, 100].map((n) => n * HIGHCHARTS_YEAR_MS),
]

function tickIntervalFor(spanMs: number, minStepMs: number): number {
  const wanted = Math.max(minStepMs, spanMs / TARGET_TICK_COUNT)
  return HIGHCHARTS_TICK_STEPS_MS.find((step) => step >= wanted) ?? HIGHCHARTS_TICK_STEPS_MS.at(-1)!
}

function getDateGranularity(spanMs: number): { dateType: DateType; tickIntervalMs: number } {
  const spanDays = spanMs / DAY_MS
  if (spanDays <= 45) {
    return { dateType: DateType.Daily, tickIntervalMs: tickIntervalFor(spanMs, DAY_MS) }
  }
  if (spanDays <= 400) {
    return { dateType: DateType.Weekly, tickIntervalMs: tickIntervalFor(spanMs, WEEK_MS) }
  }
  return {
    dateType: DateType.Monthly,
    tickIntervalMs: tickIntervalFor(spanMs, HIGHCHARTS_MONTH_MS),
  }
}

function getLabelFormat(
  dateType: DateType,
  tickIntervalMs: number,
  crossesYear: boolean,
): DateFormatType {
  if (dateType === DateType.Monthly) return DateFormatType.MonthYear
  if (tickIntervalMs >= HIGHCHARTS_MONTH_MS) {
    return crossesYear ? DateFormatType.MonthYear : DateFormatType.Month
  }
  return crossesYear ? DateFormatType.FullDateShort : DateFormatType.DayMonth
}

interface PieChartResult {
  type: typeof ChartType.Pie
  data: PieChartData[]
  height: number
  disableAnimation: boolean
  noLegend: boolean
  title?: string
  showCurrency: boolean
}

type ChatChartProps = BarChartResult | LineChartResult | PieChartResult

export function chatChartToSuperChartProps(
  chart: ChatChartData,
  hakiChartColors: string[],
): ChatChartProps {
  const singleSeries = chart.series.length === 1

  if (chart.type === "bar") {
    const colors = chart.series.map((_, i) => hakiChartColors[i % hakiChartColors.length])
    const data: CategoryChartSeries[] = chart.series.map((s, i) => ({
      name: s.name,
      type: "column" as const,
      data: s.data.map((d) => ({ category: d.label, value: d.value })),
      colors: [colors[i]!],
      ...(s.yAxis !== undefined ? { yAxis: s.yAxis } : {}),
    }))
    return {
      type: ChartType.CategorySeries,
      data,
      height: 200,
      disableAnimation: true,
      noLegend: singleSeries,
      legendAlignment: singleSeries ? undefined : LegendAlignment.Bottom,
      title: chart.title,
      showCurrency: false,
      autoFitXLabels: true,
    }
  }

  if (chart.type === "line" || chart.type === "area") {
    const seriesType = chart.type === "area" ? ("areaspline" as const) : ("spline" as const)
    const colors = chart.series.map((_, i) => hakiChartColors[i % hakiChartColors.length])
    const data: DateChartSeries[] = chart.series.map((s, i) => ({
      name: s.name,
      type: seriesType,
      data: s.data
        .map((d) => ({ milliseconds: new Date(d.label).getTime(), value: d.value }))
        .filter((d) => !isNaN(d.milliseconds))
        .sort((a, b) => a.milliseconds - b.milliseconds),
      colors: [colors[i]!],
      ...(s.yAxis !== undefined ? { yAxis: s.yAxis } : {}),
    }))

    if (chart.showTrend) {
      const trendColor = getTailwindValue("--color-muted-foreground") ?? "#888"
      const originalLen = data.length
      for (let i = 0; i < originalLen; i++) {
        const series = data[i]!
        if (series.data.length < 2) continue
        const points = series.data.map((d) => ({ x: d.milliseconds, y: d.value ?? 0 }))
        const { slope, intercept } = calculateLinearRegression(points)
        const firstX = series.data[0]!.milliseconds
        const lastX = series.data[series.data.length - 1]!.milliseconds
        data.push({
          name: `${series.name} Trend`,
          type: "spline" as const,
          data: [
            { milliseconds: firstX, value: Math.round(slope * firstX + intercept) },
            { milliseconds: lastX, value: Math.round(slope * lastX + intercept) },
          ],
          colors: [trendColor],
          dashStyle: "Dash",
          lineWidth: 2,
          ...(series.yAxis !== undefined ? { yAxis: series.yAxis } : {}),
        })
      }
    }

    const allMs = data.flatMap((s) => s.data.map((d) => d.milliseconds))
    const minMs = allMs.length > 0 ? Math.min(...allMs) : 0
    const maxMs = allMs.length > 0 ? Math.max(...allMs) : 0
    const spanMs = maxMs - minMs
    const { dateType, tickIntervalMs } = getDateGranularity(spanMs)
    const crossesYear =
      allMs.length > 0 && new Date(minMs).getUTCFullYear() !== new Date(maxMs).getUTCFullYear()
    const labelFormat = getLabelFormat(dateType, tickIntervalMs, crossesYear)

    const hasLegend = data.length > 1
    return {
      type: ChartType.TimeSeries,
      data,
      height: 200,
      disableAnimation: true,
      noLegend: !hasLegend,
      legendAlignment: hasLegend ? LegendAlignment.Bottom : undefined,
      dateType,
      title: chart.title,
      showCurrency: false,
      xAxisLabelFormatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
        return formatDate(this.value as number, labelFormat)
      },
      xAxisTickInterval: tickIntervalMs,
      xAxisLabelOverflow: "justify",
    }
  }

  // pie
  const firstSeries = chart.series[0]!
  const colors = firstSeries.data.map((_, i) => hakiChartColors[i % hakiChartColors.length])
  const data: PieChartData[] = firstSeries.data.map((d, i) => ({
    name: d.label,
    data: d.value,
    color: colors[i]!,
  }))
  return {
    type: ChartType.Pie,
    data,
    height: 200,
    disableAnimation: true,
    noLegend: false,
    title: chart.title,
    showCurrency: false,
  }
}
